import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useTemplateFilterOptionsFromMyRfis } from "../../../custom-hooks/useCatalogFilterOptions";
import { useGridExportRegistration } from "../../../custom-hooks/useGridExportRegistration";
import useRequestListStickyFilters from "../../../custom-hooks/useRequestListStickyFilters";
import { mapQuestionnaireTemplateToFilterOption } from "../../../utils/catalogFilterOptions";
import {
  useGetListForSponsorFilters,
  useGetVendorRfis,
  useGetVendorRfisAllPages,
  useListOfQuestionnaireTemplate,
} from "../../../custom-hooks/queries";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import SkeletonLoader from "../../../components/SkeletonLoader";
import VendorRequestedRfiGridTable from "../../../components/RFIs/VendorRequestedRfiGridTable";
import Pagination from "../../../components/CustomPagination";
import Notification from "../../../components/Notification";
import PageToolbarFilters from "../../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../../components/Filters/ToolbarSearch";
import TableExportMenu from "../../../components/Menus/TableExportMenu";
import { rfiCellStyles } from "../../../utils/exportBadgeStyles";
import { sortMyRfiItems } from "../../../utils/myRfiGridRowMapper";
import {
  buildVendorRequestedRfisExportData,
  PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT,
  sortConfigToVendorRequestedApiSortParams,
} from "../../../utils/vendorRequestedRfiGridRowMapper";
import { REQUEST_LIST_SCREEN_KEYS } from "../../../utils/requestListStickyFilterPersistence";
import { resolveRfiRowDetailsPath } from "../../../utils/gridOrgNameLinks";

const VENDOR_VQQ_EMPTY_SELECTED_IDS = {
  auditor: [],
  sponsor: [],
  status: [],
  templates: [],
  services: [],
};

const VENDOR_VQQ_DEFAULT_SORT = {
  requestDate: { direction: "desc" },
};

export const ProviderRequestedRfis = () => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const { data: sponsors } = useGetListForSponsorFilters();
  const { data: questionnares } = useListOfQuestionnaireTemplate();

  const { data: allVendorRfisForTemplates, isFetched: vendorRfisAllPagesFetched } =
    useGetVendorRfisAllPages("", [], [], [], []);

  const templatesFilterOptions = useTemplateFilterOptionsFromMyRfis(
    questionnares,
    allVendorRfisForTemplates?.items,
    vendorRfisAllPagesFetched,
    mapQuestionnaireTemplateToFilterOption
  );

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["vendorRfis"]
    ) || "";

  const {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
    dueDateFilter,
    setDueDateFilter,
  } = useRequestListStickyFilters({
    screenKey: REQUEST_LIST_SCREEN_KEYS.VENDOR_VQQS,
    emptySelectedIds: VENDOR_VQQ_EMPTY_SELECTED_IDS,
    defaultSortConfig: VENDOR_VQQ_DEFAULT_SORT,
    searchQuery,
    includeDueDate: true,
    useServerSort: PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT,
  });

  const serverSortParams = useMemo(() => {
    if (!PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT) return undefined;
    return sortConfigToVendorRequestedApiSortParams(sortConfig);
  }, [sortConfig]);

  const servicesIdListForApi = useMemo(
    () => [
      ...(selectedIds.services || []),
      ...(selectedIds.templates || []),
    ],
    [selectedIds.services, selectedIds.templates]
  );

  const {
    data: rfisData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetVendorRfis(
    currentPage,
    21,
    searchQuery,
    selectedIds.sponsor || [],
    [],
    selectedIds.auditor || [],
    selectedIds.status || [],
    servicesIdListForApi,
    [],
    serverSortParams,
    sortFetchNonce,
    undefined,
    dueDateFilter
  );

  const filters = useMemo(
    () => [
      {
        id: "sponsor",
        name: "Sponsor",
        searchable: true,
        options: sponsors || [],
        total: sponsors?.length || 0,
      },
      {
        id: "templates",
        name: "Templates",
        searchable: true,
        options: templatesFilterOptions,
        total: templatesFilterOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Sent", label: "Sent", checked: false },
          { statusID: "In Progress", label: "In Progress", checked: false },
          { statusID: "In Review", label: "In Review", checked: false },
          {
            statusID: "In Remediation (Vendor)",
            label: "In Remediation (Vendor)",
            checked: false,
          },
          {
            statusID: "In Remediation (Purchaser)",
            label: "In Remediation (Purchaser)",
            checked: false,
          },
          { statusID: "Complete", label: "Complete", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
        ],
        total: 6,
      },
      {
        id: "dueDate",
        name: "Due Date",
        type: "date",
        value: dueDateFilter,
        onChange: setDueDateFilter,
      },
    ],
    [sponsors, templatesFilterOptions, dueDateFilter]
  );

  const sortedItems = useMemo(() => {
    const items = rfisData?.items || [];
    if (PROVIDER_REQUESTED_RFIS_USE_SERVER_SORT) {
      return items;
    }
    return sortMyRfiItems(items, sortConfig);
  }, [rfisData?.items, sortConfig]);

  const navigate = useNavigate();

  const exportDataRef = useRef(null);
  const handleExportDataReady = useCallback((fn) => {
    exportDataRef.current = fn;
  }, []);

  useGridExportRegistration(
    handleExportDataReady,
    () =>
      buildVendorRequestedRfisExportData({
        searchQuery,
        selectedIds,
        serverSortParams,
        sortConfig,
        dueDateFilter,
      }),
    [searchQuery, selectedIds, serverSortParams, sortConfig, dueDateFilter, handleExportDataReady]
  );

  const handleRowClick = useCallback(
    (item) => {
      const path = resolveRfiRowDetailsPath(item);
      if (path) navigate(path);
    },
    [navigate]
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">My VQQs</h1>
            <p className="mt-1 text-sm text-gray-500">
              The below table displays all active and historical Vendor Qualification Questionnaire (VQQ) requests.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4 sm:mt-0">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="VQQ Requests"
              reportSubtitle="All active and historical VQQ requests"
              filePrefix="VQQ_Requests"
              downloadType="VQQ Requests"
              cellStyles={rfiCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({ pageKey: "vendorRfis", searchText: text })
                )
              }
              placeholder="Search your VQQ request details..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <VendorRequestedRfiGridTable
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={handleRowClick}
            refetch={refetch}
            isSearchActive={!!searchQuery}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={rfisData?.total_pages || 1}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
